import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PlanContainerLocationCount } from 'planning-portal-library/src/lib/core/domain/models/plan-container-location-count';
import { PlanContainerLocationCountByProduct } from 'planning-portal-library/src/lib/core/domain/models/plan-container-location-count-by-product';
import { TimeZones } from 'planning-portal-library/src/lib/core/domain/models/time-zones';
import { CommitPlanRequest } from 'planning-portal-library/src/lib/core/domain/requests/master-plan.ts/commit-plan.request';
import { injectEnvironment } from 'planning-portal-library/src/lib/core/environments/environment.token';
import {
  catchError,
  forkJoin,
  map,
  mergeMap,
  Observable,
  reduce,
  share,
} from 'rxjs';
import {
  AddMasterPlanLocationRequest,
  AddMasterPlanRequest,
  EditMasterPlanLocationRequest,
  EditMasterPlanRequest,
  GetMasterPlanLocationLookupRequest,
  GetMasterPlanLocationsRequest,
  GetMasterPlansRequest,
  MasterPlan,
  MasterPlanLocation,
  PrivateLocatioDetails,
} from '../../../../core/domain';
import { MasterPlansRepository } from '../../../../core/repositories';
import { BaseGridResponse } from '../../../shared/base-grid.response';
import { BaseRequest } from '../../../shared/base.request';
import { BaseResponse } from '../../../shared/base.response';
import {
  MasterPlanLocationWebEntity,
  MasterPlanLocationWebRespositoryMapper,
  MasterPlanWebEntity,
  MasterPlanWebRepositoryMapper,
} from './entities';
import { MasterPlanLocationFilterWebRepositoryMapper } from './entities/master-plan-location-filter-web-entity-mapper';
import { MasterPlanTimeZonesRepositoryMapper } from './entities/master-plan-time-zone-web-repository-mapper';
import { PlanContainerLocationCountByProductWebEntity } from './entities/plan-container-location-count-by-product-web-entity';
import { PlanContainerLocationCountByProductWebRepositoryMapper } from './entities/plan-container-location-count-by-product-web-repository-mapper';
import { PlanContainerLocationCountWebRepositoryMapper } from './entities/plan-container-location-count-web-repository-mapper';
import { PlanContainerLocationCountWebEntity } from './entities/plan-contianer-location-count-web-entity';
import { MasterPlanUrls } from './master-plan-api-urls';
import { MasterPlanType } from 'planning-portal-library/src/lib/core/domain/models/master-plan-type';
import { MasterPlanTypesRepositoryMapper } from './entities/master-plan-type-web-repository-mapper';
import { HDMasterPlans } from 'planning-portal-library/src/lib/core/domain/models/master-plans/hd-master-plan';

@Injectable({ providedIn: 'root' })
export class MasterPlanWebRepository extends MasterPlansRepository {
  private readonly masterPlanMapper = new MasterPlanWebRepositoryMapper();
  private readonly masterPlanLocationMapper =
    new MasterPlanLocationWebRespositoryMapper();
  private readonly PlanContainerLocationCountMapper =
    new PlanContainerLocationCountWebRepositoryMapper();
  masterPlans: MasterPlanWebEntity[] = [];
  private readonly PlanContainerLocationCountByProductMapper =
    new PlanContainerLocationCountByProductWebRepositoryMapper();
  private readonly masterPlanTypeMapper =
    new MasterPlanTypesRepositoryMapper();
  private readonly masterPlanTimeZoneMapper = new MasterPlanTimeZonesRepositoryMapper();
  private readonly environment = injectEnvironment();

  constructor(private readonly http: HttpClient) {
    super();
  }

  getPlanContainerLocations(
    request: BaseRequest<GetMasterPlanLocationsRequest>
  ): Observable<BaseGridResponse<MasterPlanLocation>> {
    const httpMap = this.http
      .post<BaseResponse<BaseGridResponse<MasterPlanLocationWebEntity>>>(
        `${this.environment.url}${MasterPlanUrls.GetPlanContainerLocations}`,
        request.request
      )
      .pipe(share());

    const dataResultMap = httpMap
      .pipe(
        mergeMap((response) => response.result!.data),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.masterPlanLocationMapper.mapFrom))
      .pipe(
        reduce(
          (items: MasterPlanLocation[], item: MasterPlanLocation) => [
            ...items,
            item,
          ],
          []
        )
      );
    const totalRecordsResultMap = httpMap.pipe(
      map((response) => response.result!.totalRecords)
    );
    return forkJoin([dataResultMap, totalRecordsResultMap]).pipe(
      map((input) => {
        const item: BaseGridResponse<MasterPlanLocation> = {
          data: input[0],
          totalRecords: +input[1],
        };
        return item;
      })
    );
  }

  getMasterPlans(
    request: BaseRequest<GetMasterPlansRequest>
  ): Observable<BaseGridResponse<MasterPlan>> {
    const httpMap = this.http
      .post<BaseResponse<BaseGridResponse<MasterPlanWebEntity>>>(
        `${this.environment.url}${MasterPlanUrls.GetPlanContainers}`,
        request.request
      )
      .pipe(share());
    const dataResultMap = httpMap
      .pipe(
        mergeMap((response) => response.result!.data),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.masterPlanMapper.mapFrom))
      .pipe(
        reduce((items: MasterPlan[], item: MasterPlan) => [...items, item], [])
      );

    const totalRecordsResultMap = httpMap.pipe(
      map((response) => response.result!.totalRecords)
    );
    return forkJoin([dataResultMap, totalRecordsResultMap]).pipe(
      map((input) => {
        const item: BaseGridResponse<MasterPlan> = {
          data: input[0],
          totalRecords: +input[1],
        };
        return item;
      })
    );
  }

  addMasterPlan(
    request: AddMasterPlanRequest
  ): Observable<BaseResponse<string>> {
    return this.http
      .post<BaseResponse<string>>(
        `${this.environment.url}${MasterPlanUrls.CreatePlanContainer}`,
        request
      )
      .pipe(
        map((data) => {
          return data;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }



  commitPlan(request: CommitPlanRequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${MasterPlanUrls.CommitPlan}`,
        request
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  editMasterPlan(request: EditMasterPlanRequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${MasterPlanUrls.EditPlanContainer}`,
        request
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  deleteMasterPlan(request: string): Observable<boolean> {
    return this.http
      .post<BaseResponse<unknown>>(
        `${this.environment.url}${MasterPlanUrls.DeleteMasterPlan}`,
        {},
        {
          params: {
            Id: request,
          },
        }
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  addMasterPlanLocation(
    request: AddMasterPlanLocationRequest
  ): Observable<boolean> {
    return this.http
      .post<BaseResponse<string>>(
        `${this.environment.url}${MasterPlanUrls.CreateMasterPlanLocation}`,
        request
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }
  getLocationCountByLocationType(
    request: string
  ): Observable<PlanContainerLocationCount> {
    return this.http
      .get<BaseResponse<PlanContainerLocationCountWebEntity>>(
        `${this.environment.url}${MasterPlanUrls.GetLocationCountByLocationType}`,
        {
          params: {
            id: request,
          },
        }
      )
      .pipe(
        map(
          (response: BaseResponse<PlanContainerLocationCountWebEntity>) =>
            response.result!
        ),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.PlanContainerLocationCountMapper.mapFrom));
  }
  getLocationCountByProduct(
    request: string
  ): Observable<PlanContainerLocationCountByProduct> {
    return this.http
      .get<BaseResponse<PlanContainerLocationCountByProductWebEntity>>(
        `${this.environment.url}${MasterPlanUrls.GetLocationCountByProduct}`,
        {
          params: {
            id: request,
          },
        }
      )
      .pipe(
        map(
          (
            response: BaseResponse<PlanContainerLocationCountByProductWebEntity>
          ) => response.result!
        ),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.PlanContainerLocationCountByProductMapper.mapFrom));
  }

  getMasterPlanTypes(): Observable<MasterPlanType[]> {
    return this.http
    .get<BaseResponse<MasterPlanType[]>>(
      `${this.environment.url}${MasterPlanUrls.GetMasterPlanTypes}`
    )
    .pipe(
      mergeMap((response: BaseResponse<MasterPlanType[]>) => response.result!),
      catchError((err) => {
        throw err;
      })
    )
    .pipe(map(this.masterPlanTypeMapper.mapFrom))
    .pipe(
      reduce((items: MasterPlanType[], item: MasterPlanType) => [...items, item], [])
    );
  }
  getMasterPlanLocations(
    request: GetMasterPlanLocationLookupRequest
  ): Observable<MasterPlanLocation[]> {
    const httpMap = this.http.post<BaseResponse<MasterPlanLocation[]>>(
      `${this.environment.url}${MasterPlanUrls.GetMasterPlanLocations}`,
      request
    );
    return httpMap
      .pipe(
        mergeMap((response) => response.result!),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.masterPlanLocationMapper.mapFrom))
      .pipe(
        reduce(
          (items: MasterPlanLocation[], item: MasterPlanLocation) => [
            ...items,
            item,
          ],
          []
        )
      );
  }
  getMasterPlanById(request: string): Observable<MasterPlan> {
    const httpMap = this.http.get<BaseResponse<MasterPlanWebEntity>>(
      `${this.environment.url}${MasterPlanUrls.GetPlanContainerById}`,
      {
        params: {
          id: request,
        },
      }
    );
    return httpMap
      .pipe(
        map((response: BaseResponse<MasterPlanWebEntity>) => response.result!),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.masterPlanMapper.mapFrom));
  }
  getMasterPlanByPlanId(request: string): Observable<MasterPlan> {
    const httpMap = this.http.get<BaseResponse<MasterPlanWebEntity>>(
      `${this.environment.url}${MasterPlanUrls.GetPlanContainerByPlanId}`,
      {
        params: {
          planId: request,
        },
      }
    );
    return httpMap
      .pipe(
        map((response: BaseResponse<MasterPlanWebEntity>) => response.result!),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.masterPlanMapper.mapFrom));
  }
  getMasterLocationById(request: string): Observable<MasterPlanLocation> {
    return this.http
      .get<BaseResponse<MasterPlanLocationWebEntity>>(
        `${this.environment.url}${MasterPlanUrls.GetPlanContainerLocationById}`,
        {
          params: {
            id: request,
          },
        }
      )
      .pipe(
        map(
          (response: BaseResponse<MasterPlanLocationWebEntity>) =>
            response.result!
        ),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.masterPlanLocationMapper.mapFrom));
  }

  editMasterPlanLocation(
    request: EditMasterPlanLocationRequest
  ): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${MasterPlanUrls.EditPlanContainerLocation}`,
        request
      )
      .pipe(
        map((data: BaseResponse<boolean>) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }


getMasterLocationDetailsById(request: string): Observable<PrivateLocatioDetails> {
    return this.http
      .get<BaseResponse<PrivateLocatioDetails>>(
        `${this.environment.url}${MasterPlanUrls.GetMasterLocationDetailsById}`,
        {
          params: {
            id: request,
          },
        }
      )
      .pipe(
        map((response: BaseResponse<PrivateLocatioDetails>) => response.result!),
        catchError((err) => {
          throw err;
        })
      );
  }

  getTimeZones(): Observable<TimeZones[]> {
    return this.http
      .get<BaseResponse<TimeZones[]>>(
        `${this.environment.url}${MasterPlanUrls.GetTimeZones}`
      )
      .pipe(
        mergeMap((response: BaseResponse<TimeZones[]>) => response.result!),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.masterPlanTimeZoneMapper.mapFrom))
      .pipe(
        reduce((items: TimeZones[], item: TimeZones) => [...items, item], [])
      );
  }

  getMasterPlansList(): Observable<HDMasterPlans[]> {
    return this.http
      .get<BaseResponse<HDMasterPlans[]>>(
        `${this.environment.url}${MasterPlanUrls.HDMasterPlans}`
      )
      .pipe(
        map((response: BaseResponse<HDMasterPlans[]>) => response.result!),
        catchError((err) => {
          throw err;
        })
      );
  }
}